import { openDB, type DBSchema, type IDBPDatabase } from "idb";

const DB_PREFIX = "phmis-offline";
const DB_VERSION = 2;
const DEFAULT_NAMESPACE = "default";

export type ConfigCacheEntry<T = unknown> = {
  type: string;
  data: T;
  version?: string | number;
  hash?: string;
  updatedAt: number;
  expiresAt?: number | null;
};

export type RecordCacheEntry<T = unknown> = {
  resource: string;
  recordId: string;
  data: T;
  updatedAt: number;
  hash?: string;
};

export type SubmissionStatus = "pending" | "processing" | "failed";

export type SubmissionQueueEntry = {
  id?: number;
  resource: string;
  endpoint: string;
  method: string;
  body: unknown;
  headers?: Record<string, string>;
  createdAt?: number;
  status?: SubmissionStatus;
  priority?: number;
  retryCount?: number;
  lastAttemptAt?: number | null;
  error?: string | null;
  metadata?: Record<string, unknown> | null;
};

export type SyncIssueEntry = {
  id?: number;
  resource?: string;
  endpoint: string;
  method: string;
  body?: unknown;
  metadata?: Record<string, unknown> | null;
  error?: string | null;
  createdAt: number;
};

export type AttachmentEntry = {
  id: string;
  submissionId?: number | null;
  resource: string;
  mimeType: string;
  data: ArrayBuffer;
  createdAt: number;
  metadata?: Record<string, unknown>;
};

export type MetaEntry = {
  key: string;
  value: unknown;
  updatedAt: number;
};

interface OfflineDBSchema extends DBSchema {
  config_cache: {
    key: string;
    value: ConfigCacheEntry;
  };
  record_cache: {
    key: string;
    value: RecordCacheEntry;
    indexes: { resource: string };
  };
  submission_queue: {
    key: number;
    value: SubmissionQueueEntry & { id: number };
    indexes: { status: string };
  };
  attachments: {
    key: string;
    value: AttachmentEntry;
    indexes: { submissionId: number };
  };
  sync_issues: {
    key: number;
    value: SyncIssueEntry & { id: number };
  };
  meta: {
    key: string;
    value: MetaEntry;
  };
}

const dbPromises = new Map<string, Promise<IDBPDatabase<OfflineDBSchema>>>();
let currentNamespace = DEFAULT_NAMESPACE;

const buildDbName = () => `${DB_PREFIX}-${currentNamespace}`;

const openDatabase = (name: string) =>
  openDB<OfflineDBSchema>(name, DB_VERSION, {
    upgrade(db, oldVersion) {
      if (oldVersion < 1) {
        db.createObjectStore("config_cache");

        const recordStore = db.createObjectStore("record_cache");
        recordStore.createIndex("resource", "resource", { unique: false });

        const queueStore = db.createObjectStore("submission_queue", {
          keyPath: "id",
          autoIncrement: true,
        });
        queueStore.createIndex("status", "status", { unique: false });

        const attachmentStore = db.createObjectStore("attachments");
        attachmentStore.createIndex("submissionId", "submissionId", { unique: false });

        db.createObjectStore("meta");
      }
      if (oldVersion < 2) {
        db.createObjectStore("sync_issues", { keyPath: "id", autoIncrement: true });
      }
    },
  });

const getDatabase = () => {
  const name = buildDbName();
  let promise = dbPromises.get(name);
  if (!promise) {
    promise = openDatabase(name);
    dbPromises.set(name, promise);
  }
  return promise;
};

const buildRecordKey = (resource: string, recordId: string) => `${resource}:${recordId}`;

export const setOfflineDbNamespace = (namespace?: string | null) => {
  const normalized = namespace && namespace.trim().length > 0 ? namespace.trim() : DEFAULT_NAMESPACE;
  if (normalized === currentNamespace) return;
  currentNamespace = normalized;
};

export const closeOfflineDb = async () => {
  const name = buildDbName();
  const db = dbPromises.get(name);
  if (db) {
    (await db).close();
    dbPromises.delete(name);
  }
};

export const deleteOfflineDb = async () => {
  await closeOfflineDb();
  const name = buildDbName();
  await indexedDB.deleteDatabase(name);
};

export const saveConfigEntry = async <T = unknown>(entry: ConfigCacheEntry<T>) => {
  const db = await getDatabase();
  await db.put("config_cache", entry, entry.type);
};

export const readConfigEntry = async <T = unknown>(type: string): Promise<ConfigCacheEntry<T> | undefined> => {
  const db = await getDatabase();
  return (db.get("config_cache", type) as Promise<ConfigCacheEntry<T> | undefined>);
};

export const deleteConfigEntry = async (type: string) => {
  const db = await getDatabase();
  await db.delete("config_cache", type);
};

export const saveRecordEntry = async <T = unknown>(entry: RecordCacheEntry<T>) => {
  const db = await getDatabase();
  await db.put("record_cache", entry, buildRecordKey(entry.resource, entry.recordId));
};

export const readRecordEntry = async <T = unknown>(
  resource: string,
  recordId: string,
): Promise<RecordCacheEntry<T> | undefined> => {
  const db = await getDatabase();
  return (db.get("record_cache", buildRecordKey(resource, recordId)) as Promise<RecordCacheEntry<T> | undefined>);
};

export const listRecordEntries = async <T = unknown>(resource: string): Promise<Array<RecordCacheEntry<T>>> => {
  const db = await getDatabase();
  return db.getAllFromIndex("record_cache", "resource", resource) as Promise<Array<RecordCacheEntry<T>>>;
};

export const clearRecordEntries = async (resource?: string) => {
  const db = await getDatabase();
  if (!resource) {
    await db.clear("record_cache");
    return;
  }
  const tx = db.transaction("record_cache", "readwrite");
  const index = tx.store.index("resource");
  let cursor = await index.openCursor(resource);
  while (cursor) {
    await cursor.delete();
    cursor = await cursor.continue();
  }
  await tx.done;
};

export const enqueueSubmission = async (
  entry: SubmissionQueueEntry,
): Promise<SubmissionQueueEntry & { id: number }> => {
  const db = await getDatabase();
  const value = {
    ...entry,
    status: entry.status ?? "pending",
    createdAt: entry.createdAt ?? Date.now(),
    retryCount: entry.retryCount ?? 0,
    metadata: entry.metadata ?? null,
  };
  const id = await db.add("submission_queue", value as SubmissionQueueEntry & { id: number });
  const stored = await db.get("submission_queue", id as number);
  return (stored ??
    ({
      ...(value as SubmissionQueueEntry),
      id: id as number,
    } as SubmissionQueueEntry & { id: number }));
};

export const listQueuedSubmissions = async (
  status?: SubmissionStatus,
): Promise<Array<SubmissionQueueEntry & { id: number }>> => {
  const db = await getDatabase();
  if (status) {
    return db.getAllFromIndex("submission_queue", "status", status);
  }
  return db.getAll("submission_queue");
};

export const getQueuedSubmission = async (
  id: number,
): Promise<(SubmissionQueueEntry & { id: number }) | undefined> => {
  const db = await getDatabase();
  return (await db.get("submission_queue", id)) ?? undefined;
};

export const updateQueuedSubmission = async (
  id: number,
  patch: Partial<SubmissionQueueEntry>,
): Promise<void> => {
  const db = await getDatabase();
  const existing = await db.get("submission_queue", id);
  if (!existing) return;
  const next = {
    ...existing,
    ...patch,
  };
  await db.put("submission_queue", next);
};

export const removeQueuedSubmission = async (id: number) => {
  const db = await getDatabase();
  await db.delete("submission_queue", id);
};

export const clearSubmissionQueue = async () => {
  const db = await getDatabase();
  await db.clear("submission_queue");
};

export const saveAttachment = async (attachment: AttachmentEntry) => {
  const db = await getDatabase();
  await db.put("attachments", attachment, attachment.id);
};

export const readAttachment = async (id: string): Promise<AttachmentEntry | undefined> => {
  const db = await getDatabase();
  return db.get("attachments", id);
};

export const deleteAttachment = async (id: string) => {
  const db = await getDatabase();
  await db.delete("attachments", id);
};

export const listAttachmentsForSubmission = async (submissionId: number) => {
  const db = await getDatabase();
  return db.getAllFromIndex("attachments", "submissionId", submissionId);
};

export const recordSyncIssue = async (issue: SyncIssueEntry) => {
  const db = await getDatabase();
  const id = await db.add("sync_issues", {
    ...issue,
    createdAt: issue.createdAt || Date.now(),
  } as SyncIssueEntry & { id: number });
  return (await db.get("sync_issues", id)) as SyncIssueEntry & { id: number };
};

export const listSyncIssues = async (): Promise<Array<SyncIssueEntry & { id: number }>> => {
  const db = await getDatabase();
  return db.getAll("sync_issues");
};

export const deleteSyncIssue = async (id: number) => {
  const db = await getDatabase();
  await db.delete("sync_issues", id);
};

export const clearSyncIssues = async () => {
  const db = await getDatabase();
  await db.clear("sync_issues");
};

export const setMetaValue = async (key: string, value: unknown) => {
  const db = await getDatabase();
  await db.put("meta", { key, value, updatedAt: Date.now() }, key);
};

export const readMetaValue = async <T = unknown>(key: string): Promise<T | undefined> => {
  const db = await getDatabase();
  const entry = await db.get("meta", key);
  return entry?.value as T | undefined;
};

export const clearOfflineData = async () => {
  const db = await getDatabase();
  await Promise.all([
    db.clear("config_cache"),
    db.clear("record_cache"),
    db.clear("submission_queue"),
    db.clear("attachments"),
    db.clear("sync_issues"),
    db.clear("meta"),
  ]);
};
